import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { QUERY_PARAMS } from '../../models/consts/QueryParams.const';
import { StorageService } from '../storage/storage.service';

@Injectable({
  providedIn: 'root',
})
export class ReferrerTrackingService {
  private readonly REFERRER_LIFETIME_IN_DAYS = 10;

  constructor(private router: Router, private storageService: StorageService) {}

  // Starts capturing the "ref" query parameter on every navigation. Must be
  // called once during app startup (see APP_INITIALIZER in app.module.ts).
  public init(): void {
    this.captureReferrerFromUrl(window.location.href);

    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        )
      )
      .subscribe((event) => {
        this.captureReferrerFromUrl(event.urlAfterRedirects);
      });
  }

  // Returns the stored referrer if it hasn't expired yet, self-cleaning it
  // (and returning null) once it's older than REFERRER_LIFETIME_IN_DAYS.
  public getValidReferrer(): string {
    const stored = this.storageService.getStoredReferrer();

    if (!stored?.value) {
      return null;
    }

    const lifetimeInMs = this.REFERRER_LIFETIME_IN_DAYS * 24 * 60 * 60 * 1000;
    const isExpired = Date.now() - stored.savedAt > lifetimeInMs;

    if (isExpired) {
      this.storageService.removeReferrer();
      return null;
    }

    return stored.value;
  }

  public clearReferrer(): void {
    this.storageService.removeReferrer();
  }

  private captureReferrerFromUrl(url: string): void {
    const queryString = url?.split('?')?.[1];

    if (!queryString?.length) {
      return;
    }

    const ref = new URLSearchParams(queryString).get(QUERY_PARAMS.REFERER);

    if (ref) {
      this.storageService.saveReferrer(ref);
    }
  }
}
